import { ChangeDetectionStrategy, Component, EventEmitter, HostListener, Output, ViewChild } from '@angular/core';
import { MatSelect } from '@angular/material/select';
import { Store } from '@ngrx/store';
import { notify } from 'app/store/common-effects/notifier.effects';
import { navigate } from 'app/store/common-effects/router.effects';
import { allAssignedProjects } from 'app/store/login/assigned-projects/assigned-projects.selectors';
import { logout } from 'app/store/login/logged-user/logged-user.actions';
import { loggedUser } from 'app/store/login/logged-user/logged-user.selectors';
import { LoggedUserState } from 'app/store/login/logged-user/logged-user.state';
import { currentProject } from 'app/store/project/current-project/current-project.selectors';
import { TokenService } from 'core/http/lg-logistic-rest-api/Auth/token.service';
import { ProjectOutput } from 'core/http/root';
import { ownedError } from 'core/utils/owned-error';
import { selectFirst } from 'core/utils/rx-common';
import { ClipboardService } from 'ngx-clipboard';
import { map, switchMap } from 'rxjs/operators';
import { SelectSearchComponent } from 'shared/modules/controls/select-search/select-search.component';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class HeaderComponent {
  @ViewChild(MatSelect)
  projectSelect!: MatSelect;

  readonly username$ = this.store$.select(loggedUser).pipe(map(user => user && user.userInfo.fullName));
  readonly projects$ = this.store$.select(allAssignedProjects);
  readonly selectedProject$ = this.store$.select(currentProject);

  @Output() clickEmitter = new EventEmitter<void>();

  constructor(
    private tokenService: TokenService,
    private store$: Store<LoggedUserState>,
    private clipboard: ClipboardService,
  ) { }

  @HostListener('document:keydown.alt.shift.p') open(): void {
    this.projectSelect.open();
  }

  navigateFromMouse(event: MouseEvent, project: ProjectOutput): void {
    if (event.which !== 2) {
      return;
    }

    event.preventDefault();
    window.open(`/project/${project.route}/orders`);
  }

  navigateToProject(route: string): void {
    this.store$.dispatch(navigate({ path: ['/', 'project', route, 'orders'] }));
  }

  copyProjectId(): void {
    this.selectedProject$.subscribe((p) => {
      this.clipboard.copy(p.id);
      this.store$.dispatch(notify({ message: 'NOTIFICATIONS.project_id_copied' }));
    });
  }

  copyUserId(): void {
    this.store$
      .pipe(selectFirst(loggedUser))
      .subscribe((user) => {
        if (!user) {
          return;
        }

        this.clipboard.copy(user.userInfo.id);
        this.store$.dispatch(notify({ message: 'NOTIFICATIONS.user_id_copied' }));
      });
  }

  logout(): void {
    this.store$.pipe(
      selectFirst(loggedUser),
      switchMap((user) => {
        if (!(user && user.accessToken)) {
          throw ownedError(new Error('Access token is missing'), 'authentication');
        }

        return this.tokenService.revoke(user.accessToken);
      }),
    ).subscribe(() => this.store$.dispatch(logout()));
  }

  focus(search: SelectSearchComponent): void {
    search.input.nativeElement.focus();
  }
}
